import {
  Shape,
  Mesh,
  MeshStandardMaterial,
  Scene,
  DoubleSide,
  ExtrudeGeometry,
  BufferAttribute,
  Material,
  ShapeGeometry,
  Color,
  Texture,
  Vector2,
} from "three";

import AssetManager from "../utils/asset_manager";
import { animateNormalMap } from "../utils/shader_utils";
import { ShapeHelper } from "../utils/shape_helpers";
import { CausticMaterial } from "../materials/causetic_material";
import SceneManager from "../scene_mgr";


class PoolBorders {
  
  ground: Mesh;
  groundOverlay:Mesh;
  causticMaterial:CausticMaterial
  poolLength: number;
  poolWidth: number;
  poolDepth: number;
  borderElevation: number;
  margin: number;
  poolType: string;

  outerShape: Shape;
  innerShape: Shape;
  timeOfDay: string = 'day';
  sceneMgr: SceneManager;
  innerColor : number = 0xffffff;

  private groundMaterial: MeshStandardMaterial;

  constructor(
    poolLength: number,
    poolWidth: number,
    poolDepth: number,

    borderElevation: number,
    poolType:string,
    margin: number = 0.2,
    sceneMgr:SceneManager
    
  ) {
    this.sceneMgr = sceneMgr;
    this.poolLength = poolLength;
    this.poolType = poolType;
    this.poolWidth = poolWidth;
    this.poolDepth = poolDepth;
    this.borderElevation = borderElevation;
    this.margin = margin;

    this.createGroundOutline(borderElevation);
  }

  addToScene(scene: Scene) {
    scene.add(this.ground);
    scene.add(this.groundOverlay);
  }

  removeFromScene(scene: Scene) {
    scene.remove(this.ground);
    scene.remove(this.groundOverlay);
  }

  resize(
    poolLength: number,
    poolWidth: number,
    poolDepth: number,
    borderElevation: number,
    scene: Scene
  ) {
    this.poolLength = poolLength;
    this.poolWidth = poolWidth;
    this.poolDepth = poolDepth;

    // Remove the old ground mesh from the scene
    scene.remove(this.ground);
    scene.remove(this.groundOverlay);

    // Create and add the new ground mesh to the scene
    this.createGroundOutline(borderElevation);
    scene.add(this.ground);
    scene.add(this.groundOverlay);

    
  }

  changePoolType(scene:Scene, poolType: string) {
    this.poolType = poolType;
    this.resize(this.poolLength, this.poolWidth, this.poolDepth, this.borderElevation, scene);
  }



  private createGroundOutline(borderElevation: number) {
    this.borderElevation = borderElevation;
    this.outerShape = ShapeHelper.createOutlineShape(this.poolLength, this.poolWidth , this.margin);
    this.buildOverlayFx();

    // Define the extrusion settings
    const extrudeSettings = {
      depth: this.borderElevation, // Set the extrusion depth based on poolHeight
      bevelEnabled: true,
      bevelThickness: 0.01,
      bevelSize: 0.01,
      bevelSegments: 2,
    };

    // Create the extruded geometry
    const groundGeometry = new ExtrudeGeometry(this.outerShape, extrudeSettings);
    groundGeometry.computeVertexNormals();

    const repeat = 2;

    const colorMap = AssetManager.getInstance().getAsset("concrete_color") as Texture;
    colorMap.repeat.set(repeat, repeat);

    const normalMap = AssetManager.getInstance().getAsset("concrete_normal") as Texture;
    normalMap.repeat.set(repeat, repeat);

    const roughnessMap = AssetManager.getInstance().getAsset("concrete_rough") as Texture;
    roughnessMap.repeat.set(repeat, repeat);
    
    const isReflective = this.poolType=='overflow' || this.poolType == 'infinity' ? true : false;

    const waterTint =0x7DBBD2
    this.groundMaterial = new MeshStandardMaterial({
      metalness: isReflective ? 0.98: 0.3,
      roughness: isReflective ? 0.0 : 0.9,
      envMapIntensity: isReflective ? 1.0 : 0.5,
      toneMapped: true,
      normalMapType: 0,
      color: isReflective ? waterTint : 0x9E9E99,
      side: DoubleSide,
      map: colorMap,
    
      normalMap: isReflective ?null : normalMap,
      normalScale: isReflective ? null : new Vector2(4, 4),
      roughnessMap: isReflective ? null : null,
      bumpMap: isReflective ? null : normalMap,
      
    });

    if (this.poolType=='overflow' || this.poolType == 'infinity' ) {
      console.log('add the animate normal effect on the pool border material');
      animateNormalMap(this.groundMaterial, 10);
      this.groundMaterial.needsUpdate=true;
    }

    this.ground = new Mesh(groundGeometry, this.groundMaterial);
    this.ground.renderOrder = 2;
    this.ground.layers.set(2);

    this.ground.rotation.x = -Math.PI / 2;
    this.ground.castShadow = false;

  }

  private buildOverlayFx() {
    this.groundOverlay = new Mesh(new ShapeGeometry(this.outerShape.clone(), 4));
    this.groundOverlay.rotation.x = -Math.PI / 2;
    this.groundOverlay.translateZ(this.sceneMgr.margelleHeight +  0.02);

    const tex = AssetManager.getInstance().getAsset("causticMap") as Texture;
    tex.repeat.set(4, 4);

    this.causticMaterial = new CausticMaterial(
      tex,
      new Color(0x000000),
      new Color(0x558888),
      0.5

    );
    this.groundOverlay.material = this.causticMaterial;

    if ((this.poolType=='overflow' || this.poolType=='infinity') && this.timeOfDay=='day') {
      this.groundOverlay.visible=true;
    }
    else {
      this.groundOverlay.visible=false;

    }
  }

  accuTime:number = 0;
  updateTime(time: number) {
    this.accuTime+=0.01;

    this.causticMaterial.updateTime(this.accuTime);
    if (this.poolType=='overflow') {
     // console.log ('shader: ', this.groundMaterial.userData.shader);
      if ((this.ground.material as Material).userData.shader) {
      
        (this.ground.material as Material).userData.shader.uniforms.time.value = this.accuTime;
   //     console.log((this.ground.material as Material).userData.shader.uniforms.time.value);
      }
    }
  }

  setTimeOfDay(lightingMode: string) {
    this.timeOfDay = lightingMode;
    if (lightingMode=='day') {
      if (this.poolType=='overflow') {
        this.groundOverlay.visible=true;
      }
      else {
        this.groundOverlay.visible=false;
      }
    }
    else {
      this.groundOverlay.visible=false;
    }
  }
}

export default PoolBorders;
